<script setup lang="ts">
import { formatFloat } from '@/utils/formatFloat'

import { NewProduct, NewProductModification } from '@/types/api/api-v4/product'

type Props = {
  product: NewProduct
  currentModification: NewProductModification
}

const props = defineProps<Props>()

const { isMobile } = useResponsive()

const isNotExpand = ref(true)

const nutritionalValue = computed(() => {
  return props.currentModification.nutritional_values
})
</script>

<template>
  <div class="product-detail-description">
    <div
      class="product-detail-description__text"
      :class="{
        'product-detail-description__text--not-expand': isNotExpand && isMobile,
      }"
    >
      {{ currentModification.description }}
    </div>

    <div
      v-if="isNotExpand && isMobile"
      class="product-detail-description__btn-expand"
      @click="isNotExpand = false"
    >
      <the-icon name="expand-down" color="var(--ui-secondary)" />
    </div>

    <template v-if="!isNotExpand || !isMobile">
      <div
        v-if="product.marking_composition"
        class="product-detail-description__composition"
      >
        <div class="product-detail-description__composition-title">Состав</div>
        <div class="product-detail-description__composition-text">
          {{ product.marking_composition }}
        </div>
      </div>

      <div class="product-detail-description__colories">
        <div class="product-detail-description__colories-title">На 100 г</div>

        <div class="product-detail-description__colories-items">
          <div class="product-detail-description__colories-item">
            <div class="product-detail-description__colories-weight">
              {{ formatFloat(nutritionalValue.protein) }} г
            </div>
            <div class="product-detail-description__colories-name">белки</div>
          </div>

          <div class="product-detail-description__colories-item">
            <div class="product-detail-description__colories-weight">
              {{ formatFloat(nutritionalValue.fat) }} г
            </div>
            <div class="product-detail-description__colories-name">жиры</div>
          </div>

          <div class="product-detail-description__colories-item">
            <div class="product-detail-description__colories-weight">
              {{ formatFloat(nutritionalValue.carbohydrate) }} г
            </div>
            <div class="product-detail-description__colories-name">
              углеводы
            </div>
          </div>

          <div class="product-detail-description__colories-item">
            <div class="product-detail-description__colories-weight">
              {{ formatFloat(nutritionalValue.calorific) }}
            </div>
            <div class="product-detail-description__colories-name">кКал</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.product-detail-description {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  color: var(--ui-dark-grey);
  word-wrap: break-word;

  &__text {
    position: relative;

    &--not-expand {
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-line-clamp: 2;
      white-space: initial;
      -webkit-box-orient: vertical;
    }
  }

  &__composition {
    padding-bottom: 16px;
    margin-top: 16px;
    border-bottom: 1px solid var(--ui-grey);

    @include media('>=tablet') {
      padding: 0;
      margin-top: 24px;
      border: unset;
    }
  }

  &__composition-title {
    margin-bottom: 8px;
    font-weight: 800;
    color: var(--ui-secondary);
  }

  &__colories {
    margin-top: 14px;

    @include media('>=tablet') {
      margin-top: 24px;
    }
  }

  &__colories-title {
    margin-bottom: 8px;
    font-size: 12px;
    line-height: 16px;
  }

  &__colories-items {
    display: flex;
    gap: 24px;
    align-items: center;
  }

  &__colories-item {
    color: var(--ui-secondary);
  }

  &__colories-name {
    font-size: 12px;
    line-height: 16px;
  }

  &__btn-expand {
    display: flex;
    width: max-content;
    margin: 4px;
    margin: 0 auto;
  }
}
</style>
